/**
 * The gate: everything between "the page loaded" and "the app can be used".
 *
 * The `double-layer-auth` pattern's central rule is that these are *different* failures and
 * must look different:
 *
 *   - **No Clerk session** (Layer 2) → sign in.
 *   - **A valid session with no role here** (Layer 1) → the no-access screen, which explains
 *     what the app is and that HR grants access. **Never an automatic sign-out**: signing
 *     someone out because they lack a role teaches them their credentials are broken, and they
 *     will try again all afternoon. The Clerk session is fleet-wide, so it would also log them
 *     out of every other leychoon.app app on the way past.
 *   - **Deactivated** → the same screen, worded as revoked rather than not-yet-granted.
 *
 * And two deployment states, which are facts about the build rather than user errors: no verifier
 * wired on the API side (`auth: unwired`), and no publishable key in the bundle — the latter
 * being what a Coolify build gets when `VITE_CLERK_PUBLISHABLE_KEY` is set as a runtime variable
 * instead of a build argument.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { devIdentity, setDevIdentity } from '@/lib/api';
import type { ApiError } from '@/lib/api';
import type { Meta } from '@/lib/types';
import { Alert, Button, Card, Field, Input, Spinner } from '@/components/ui';
import {
  clerkConfigured,
  clerkEmail,
  clerkFailure,
  clerkUnreachable,
  loadClerk,
  retryClerk,
  signOutEverywhere,
} from '@/lib/clerk';

/**
 * A little longer than the loader's own deadline, so the loader gets to report its reason first
 * and this only fires for a stall nobody anticipated.
 */
const WATCHDOG_MS = 13_000;

export function Loading({ what = 'the app' }: { what?: string }) {
  return (
    <div className="text-muted-foreground flex min-h-dvh items-center justify-center gap-3 text-sm">
      <Spinner />
      Loading {what}…
    </div>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-6 p-6">
      <header>
        <p className="text-muted-foreground text-sm font-medium tracking-wide uppercase">
          Ley Choon Group · HR035
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Department Bonding</h1>
      </header>
      {children}
    </main>
  );
}

/** A valid identity with no grant here, or one that has been revoked. */
export function NoAccess({ error, email }: { error: ApiError; email: string | null }) {
  const revoked = error.code === 'deactivated';
  const signedInAs = email ?? clerkEmail();
  return (
    <Frame>
      <Card title={revoked ? 'Your access has been withdrawn' : 'You do not have access yet'}>
        <div className="grid gap-4 text-sm">
          <p className="text-muted-foreground">
            This app administers the HR035 department bonding budget — proposals before an event,
            claims after it, and the entitlement each department has to spend.
          </p>
          <p className="text-muted-foreground">
            {revoked
              ? 'Your access to this app was withdrawn. If that is wrong, HR can restore it.'
              : 'Access is granted by HR, not by signing in. Ask HR to add you and this page will work on your next visit — no new account needed.'}
          </p>
          {signedInAs ? (
            <p className="text-muted-foreground text-xs">
              Signed in as <span className="font-medium">{signedInAs}</span>
            </p>
          ) : null}
          <p className="text-muted-foreground text-xs">
            You are still signed in to every other Ley Choon app — this page has not signed you out
            of anything.
          </p>
          <p className="text-muted-foreground text-xs">{error.message}</p>
        </div>
      </Card>
      {/*
        No automatic sign-out — a manual one, because leaving is the user's decision, and because
        the Clerk session belongs to the whole fleet rather than to this app.
      */}
      <div className="flex justify-between">
        <a
          className="text-muted-foreground text-sm underline underline-offset-4"
          href="https://home.leychoon.app"
        >
          Back to Home
        </a>
        <Button
          size="sm"
          onClick={() => {
            setDevIdentity(null);
            void signOutEverywhere().finally(() => globalThis.location.reload());
          }}
        >
          Sign out
        </Button>
      </div>
    </Frame>
  );
}

/**
 * Clerk's Email Code sign-in, mounted in place.
 *
 * Clerk's own component rather than a hand-rolled form: the Email-Code flow involves a code
 * delivery, a retry, a rate limit and a bot check, and re-implementing that against their API
 * would be re-implementing it worse. The only thing this app decides is where it appears and what
 * surrounds it.
 *
 * Mounted into a plain div through the imperative SDK — no `<ClerkProvider>` — because everything
 * else in this app already reads `GET /me` for its authorization state. Adding a second source of
 * truth for "who is signed in" is how the two end up disagreeing.
 */
export function SignIn() {
  const target = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>(() =>
    clerkUnreachable() ? 'failed' : 'loading',
  );
  const [reason, setReason] = useState<string | null>(() => clerkFailure());

  /**
   * Load Clerk and hand it the node.
   *
   * Everything is inside one `try`, including `mountSignIn`. That call throws for a misconfigured
   * instance — an origin the instance does not recognise, a satellite domain with no primary, a
   * component it will not render — and an unguarded throw here left the spinner up **for ever**
   * while telling nobody. A swallowed rejection is the worst kind of failure: the screen says
   * "loading" and means "broken".
   *
   * No cancellation flag either. React 18 upwards does not warn about setting state after
   * unmount, and the flag was itself a hazard: under StrictMode's mount-cleanup-mount it could
   * discard the one attempt that actually succeeded.
   */
  const attach = useCallback(async (attempt: () => Promise<unknown>): Promise<void> => {
    setState('loading');
    setReason(null);
    try {
      const clerk = (await attempt()) as {
        mountSignIn?: (node: HTMLDivElement) => void;
      } | null;

      if (!clerk?.mountSignIn) {
        setState('failed');
        setReason(clerkFailure());
        return;
      }
      // Unmounted while we waited: the next mount will try again, and mounting into a detached
      // node would be a leak with nothing to show for it.
      if (!target.current) return;

      // Never mount twice into the same node.
      target.current.replaceChildren();
      clerk.mountSignIn(target.current);
      setState('ready');
    } catch (error) {
      setState('failed');
      setReason(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => {
    void attach(loadClerk);

    /**
     * The backstop. Whatever stalls — the chunk, the handshake, a Clerk internal that never
     * settles — the screen must stop saying "loading" and start saying something true. Every
     * known path already exits inside the deadline; this is what makes a path nobody has thought
     * of exit too.
     */
    const watchdog = setTimeout(() => {
      setState((current) => (current === 'loading' ? 'failed' : current));
      setReason((current) => current ?? 'the sign-in service did not finish loading');
    }, WATCHDOG_MS);

    return () => {
      clearTimeout(watchdog);
      target.current?.replaceChildren();
    };
  }, [attach]);

  return (
    <Frame>
      <Card title="Sign in">
        <p className="text-muted-foreground text-sm">
          One account across every Ley Choon app. Enter your work address and Clerk sends a code —
          there is no password to remember.
        </p>

        {state === 'failed' ? (
          <Alert tone="bad" title="Sign-in could not start" className="mt-4">
            <p>
              The sign-in service (<code>clerk.leychoon.app</code>) did not load. Nothing is wrong
              with your account, and nothing has been signed out.
            </p>
            {reason ? <p className="mt-2 font-mono text-xs break-words">{reason}</p> : null}
            <p className="mt-2">
              If it says the request timed out, the service or the network is at fault and it is the
              same for every Ley Choon app. Anything else is this app&rsquo;s Clerk configuration —
              worth sending that line to IT rather than trying again for long.
            </p>
            <div className="mt-3">
              <Button size="sm" onClick={() => void attach(retryClerk)}>
                Try again
              </Button>
            </div>
          </Alert>
        ) : null}

        {state === 'loading' ? (
          <p className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
            <Spinner /> Loading sign-in…
          </p>
        ) : null}

        <div ref={target} className="mt-4 flex justify-center" />
      </Card>
      <p className="text-muted-foreground text-xs">
        Access to this app is granted by HR separately from signing in. If you have an account but
        no access here, the next screen will say so.
      </p>
    </Frame>
  );
}

/**
 * The bundle has no publishable key.
 *
 * Almost always one specific mistake: `VITE_CLERK_PUBLISHABLE_KEY` set as a runtime variable in
 * Coolify rather than a build argument. The dashboard shows the value set and the bundle does not
 * contain it, so this screen says which of the two to look at.
 */
export function ClerkNotConfigured({ meta }: { meta: Meta }) {
  return (
    <Frame>
      <Alert tone="bad" title="Sign-in is not available on this build">
        <p>
          This bundle was built without <code>VITE_CLERK_PUBLISHABLE_KEY</code>, so there is no
          sign-in to show. The value is <strong>build-time</strong>: it is compiled into the
          JavaScript, so setting it as a runtime-only variable changes nothing until the app is
          rebuilt.
        </p>
        <p className="mt-2">
          In Coolify, the variable must be marked as a <strong>build variable</strong>, and the
          image rebuilt — a restart will not do it.
        </p>
      </Alert>
      <Card title="Build">
        <dl className="grid grid-cols-[8rem_1fr] gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Version</dt>
          <dd className="tabular">{meta.version}</dd>
          <dt className="text-muted-foreground">Environment</dt>
          <dd>{meta.env}</dd>
          <dt className="text-muted-foreground">API verifier</dt>
          <dd>{meta.auth}</dd>
          <dt className="text-muted-foreground">Publishable key</dt>
          <dd>{clerkConfigured ? 'present' : 'MISSING'}</dd>
        </dl>
      </Card>
    </Frame>
  );
}

/** The API has no verifier configured, so it refuses every request by design. */
export function AuthNotWired({ meta }: { meta: Meta }) {
  return (
    <Frame>
      <Alert tone="warn" title="This deployment cannot verify anybody">
        <p>
          The API has no identity verifier configured (<code>CLERK_JWKS_URL</code> /{' '}
          <code>CLERK_EXPECTED_ISS</code>), so it refuses every request — deliberately, failing
          closed rather than open. The startup banner names what it read.
        </p>
      </Alert>
      <Card title="Build">
        <dl className="grid grid-cols-[8rem_1fr] gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">Version</dt>
          <dd className="tabular">{meta.version}</dd>
          <dt className="text-muted-foreground">Environment</dt>
          <dd>{meta.env}</dd>
          <dt className="text-muted-foreground">Verifier</dt>
          <dd>{meta.auth}</dd>
        </dl>
      </Card>
    </Frame>
  );
}

/**
 * The dev identity picker — how the app is used before Clerk exists.
 *
 * It sends `X-Dev-Email`, which only the fenced dev stub reads, and that stub only loads with
 * `AUTH_DEV_BYPASS=true` and `APP_ENV=dev` (`auth/dev-identity.ts`). On any other build the
 * header is inert, which is why this screen can exist at all without being a way in.
 */
export function DevSignIn({ meta }: { meta: Meta }) {
  const [email, setEmail] = useState(devIdentity() ?? '');

  return (
    <Frame>
      <Alert tone="bad" title="Development sign-in">
        This build trusts an email address with no password and no verification. It exists so the
        app can be built before Clerk is wired, and it cannot start outside <code>APP_ENV=dev</code>
        .
      </Alert>
      <Card title="Act as">
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setDevIdentity(email.trim() || null);
            globalThis.location.reload();
          }}
        >
          <Field
            label="Email address"
            hint="Whoever this is must already be allowlisted or hold a role."
          >
            {(props) => (
              <Input
                {...props}
                type="email"
                autoComplete="off"
                placeholder="hr@leychoon.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <div className="flex justify-end">
            <Button type="submit" variant="primary" disabled={email.trim() === ''}>
              Continue
            </Button>
          </div>
        </form>
      </Card>
      <p className="text-muted-foreground text-xs">
        {meta.app} {meta.version} · {meta.env} · verifier {meta.auth}
      </p>
    </Frame>
  );
}

/** Something genuinely broke — a 500, a network failure, an unreadable response. */
export function Broken({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <Frame>
      <Alert tone="bad" title="The app could not start">
        <p>{message}</p>
        <p className="mt-2">
          If this persists, the API or its database may be down. <code>/readyz</code> says which.
        </p>
      </Alert>
      <div className="flex justify-end">
        <Button onClick={() => globalThis.location.reload()}>Try again</Button>
      </div>
    </Frame>
  );
}
